import { Box, useMediaQuery, useTheme } from "@mui/material";
import { Coverage } from "fhir/r5";

export type InsuranceCardsTab = "active" | "inactive";

export const INSURANCE_CARDS_TABS: {
  value: InsuranceCardsTab;
  label: string;
  shortLabel: string;
  description: string;
}[] = [
  {
    value: "active",
    label: "Cartões ativos",
    shortLabel: "Ativos",
    description: "Cartões disponíveis para uso em consultas e agendamentos.",
  },
  {
    value: "inactive",
    label: "Cartões inativos",
    shortLabel: "Inativos",
    description: "Cartões desativados. Reative quando precisar usar novamente.",
  },
];

export function isCoverageActive(coverage: Coverage): boolean {
  return (coverage.status || "active") === "active";
}

export function splitCoveragesByStatus(coverages: Coverage[]) {
  const active = coverages.filter(isCoverageActive);
  const inactive = coverages.filter((item) => !isCoverageActive(item));
  return { active, inactive };
}

interface InsuranceCardsSegmentedTabsProps {
  value: InsuranceCardsTab;
  onChange: (tab: InsuranceCardsTab) => void;
  activeCount: number;
  inactiveCount: number;
  compact?: boolean;
}

/** Abas Ativos / Inativos — pill, sem scroll horizontal. */
export function InsuranceCardsSegmentedTabs({
  value,
  onChange,
  activeCount,
  inactiveCount,
  compact = false,
}: InsuranceCardsSegmentedTabsProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  const counts: Record<InsuranceCardsTab, number> = {
    active: activeCount,
    inactive: inactiveCount,
  };

  return (
    <Box
      role="tablist"
      aria-label="Filtrar cartões por status"
      sx={{
        display: "flex",
        gap: 0.5,
        width: "100%",
        bgcolor: (t) => t.palette.action.hover,
        borderRadius: 2.5,
        p: 0.5,
        mb: compact ? 0.75 : 1,
      }}
    >
      {INSURANCE_CARDS_TABS.map((tab) => {
        const selected = value === tab.value;
        const count = counts[tab.value];
        const displayLabel = isMobileLayout ? tab.shortLabel : tab.label;

        return (
          <Box
            key={tab.value}
            role="tab"
            aria-selected={selected}
            component="button"
            type="button"
            onClick={() => onChange(tab.value)}
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              border: "none",
              cursor: "pointer",
              borderRadius: 2,
              py: compact ? 0.65 : 0.85,
              px: isMobileLayout ? 0.5 : 0.75,
              fontFamily: "inherit",
              fontSize: isMobileLayout ? "0.75rem" : "0.8125rem",
              fontWeight: 700,
              lineHeight: 1.25,
              textAlign: "center",
              transition:
                "background-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease",
              minHeight: compact ? 36 : 44,
              bgcolor: selected ? "background.paper" : "transparent",
              color: selected ? "primary.main" : "text.secondary",
              boxShadow: selected ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            {displayLabel}
            {count > 0 ? ` (${count})` : ""}
          </Box>
        );
      })}
    </Box>
  );
}

export function InsuranceCardsTabHelperText({ tab }: { tab: InsuranceCardsTab }) {
  const description = INSURANCE_CARDS_TABS.find((t) => t.value === tab)?.description;
  if (!description) return null;

  return (
    <Box
      component="p"
      sx={{
        m: 0,
        mb: 1.25,
        px: 0.25,
        fontSize: "0.75rem",
        lineHeight: 1.4,
        fontWeight: 500,
        color: "text.secondary",
      }}
    >
      {description}
    </Box>
  );
}
